import React, { useMemo } from "react";
import { ShipmentErrorChip } from "./ShipmentErrorChip";
import type { ShipmentFeedError } from "@services/shipments/types";

interface BundledError {
  type: string;
  message: string;
  resolved: boolean;
  count: number;
  errorIds: string[];
  representativeError: ShipmentFeedError;
}

interface ShipmentDashboardErrorsProps {
  errors: ShipmentFeedError[];
  onResolve: (errorIds: string[], revert?: boolean) => void;
  shipmentId: string;
}

export function ShipmentDashboardErrors({
  errors,
  onResolve,
  shipmentId,
}: ShipmentDashboardErrorsProps) {
  const bundledErrors = useMemo(() => {
    // Group errors by type, message, and resolved state
    const errorGroups = new Map<string, BundledError>();

    errors.forEach((error) => {
      const key = `${error.type}-${error.message}-${error.resolved || false}`;

      if (errorGroups.has(key)) {
        const existing = errorGroups.get(key)!;
        existing.count++;
        existing.errorIds.push(error.id || "");
      } else {
        errorGroups.set(key, {
          type: error.type,
          message: error.message,
          resolved: error.resolved || false,
          count: 1,
          errorIds: [error.id || ""],
          representativeError: error,
        });
      }
    });

    // Convert to array and sort alphabetically by type + message
    return Array.from(errorGroups.values()).sort((a, b) => {
      const aKey = `${a.type}-${a.message}`;
      const bKey = `${b.type}-${b.message}`;
      return aKey.localeCompare(bKey);
    });
  }, [errors]);

  const handleBundledResolve = (
    bundledError: BundledError,
    revert?: boolean
  ) => {
    // Resolve/unresolve all errors in the bundle with a single API call
    const validErrorIds = bundledError.errorIds.filter(Boolean);
    if (validErrorIds.length > 0) {
      onResolve(validErrorIds, revert);
    }
  };

  return (
    <>
      {bundledErrors.map((bundledError, index) => (
        <ShipmentErrorChip
          key={`${bundledError.type}-${bundledError.message}-${bundledError.resolved}-${index}`}
          error={bundledError.representativeError}
          count={bundledError.count}
          onResolve={(errorId, revert) =>
            handleBundledResolve(bundledError, revert)
          }
        />
      ))}
    </>
  );
}
